'use client';
import { useState, useEffect, useMemo } from 'react';
import { fuzzyFilter } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import type { LandingTab } from '../types/landing.types';
import { MOCK_LANDING_PLANS, MOCK_TESTIMONIALS } from '../constants/landing.constants';

const PAGE_SIZE = 10;

const LANDING_FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Tipo',
		options: [
			{ value: 'tipo:Consultor', label: 'Consultor' },
			{ value: 'tipo:Agricultor', label: 'Agricultor' },
			{ value: 'tipo:Gestor', label: 'Gestor' },
		],
	},
	{
		label: 'Status',
		options: [
			{ value: 'status:active', label: 'Ativo' },
			{ value: 'status:inactive', label: 'Inativo' },
		],
	},
];

export const useLandingPage = () => {
	const [activeTab, setActiveTabState] = useState<LandingTab>('plans');
	const [isLoading, setIsLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [page, setPage] = useState(1);

	useEffect(() => {
		const t = setTimeout(() => setIsLoading(false), 300);
		return () => clearTimeout(t);
	}, []);

	useEffect(() => {
		setPage(1);
	}, [searchQuery, filterValues]);

	const setActiveTab = (tab: LandingTab) => {
		setActiveTabState(tab);
		setSearchQuery('');
		setFilterValues([]);
		setPage(1);
	};

	const statusFilters = filterValues.filter(v => v.startsWith('status:')).map(v => v.slice(7));
	const tipoFilters = filterValues.filter(v => v.startsWith('tipo:')).map(v => v.slice(5));

	const processedPlans = useMemo(() => {
		let result = fuzzyFilter(MOCK_LANDING_PLANS, searchQuery, p => [p.name, p.tipo].filter(Boolean).join(' '));
		if (statusFilters.length > 0) result = result.filter(p => statusFilters.includes(p.status));
		if (tipoFilters.length > 0) result = result.filter(p => tipoFilters.includes(p.tipo));
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [searchQuery, filterValues, sortAsc]);

	const processedTestimonials = useMemo(() => {
		let result = fuzzyFilter(MOCK_TESTIMONIALS, searchQuery, t => [t.name, t.tipo].filter(Boolean).join(' '));
		if (statusFilters.length > 0) result = result.filter(t => statusFilters.includes(t.status));
		if (tipoFilters.length > 0) result = result.filter(t => tipoFilters.includes(t.tipo));
		if (sortAsc) result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		return result;
	}, [searchQuery, filterValues, sortAsc]);

	const totalPages = useMemo(() => {
		const len = activeTab === 'plans' ? processedPlans.length : processedTestimonials.length;
		return Math.max(1, Math.ceil(len / PAGE_SIZE));
	}, [activeTab, processedPlans.length, processedTestimonials.length]);

	const filteredPlans = useMemo(
		() => processedPlans.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedPlans, page]
	);

	const filteredTestimonials = useMemo(
		() => processedTestimonials.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedTestimonials, page]
	);

	return {
		activeTab,
		setActiveTab,
		filteredPlans,
		filteredTestimonials,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc(v => !v),
		filterValues,
		setFilterValues,
		filterGroups: LANDING_FILTER_GROUPS,
		page,
		setPage,
		totalPages,
		isLoading,
	};
};
